import { sendNotification, type SendNotificationResponse } from "../../../../api/sendNotification";
import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../../stores";

/**
 * Track sessions where compliance event has already been sent to prevent duplicate calls.
 * This prevents re-sending the event if sendNotification fails and the expert retries.
 * Module-level to persist across component unmounts/remounts.
 */
const complianceEventSentSessions = new Set<string>();

export const useHandleSendLink = ({ isGuidedBrowsing }: { isGuidedBrowsing?: boolean } = {}) => {
    const {
        sessionId,
        agentId,
        partner,
        logger,
        analytics,
        taskType,
        taskData,
        activeChatId,
        apiClients,
        gaiaWebsocket,
        features,
    } = useSalesContext();

    const isRealTimeComplianceCheckEnabled = features.isRealTimeComplianceCheckEnabled;

    const primaryProducts = useProductOfferStore((state) => state.getPrimaryProducts(sessionId));
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));

    if (primaryProducts == null) {
        throw new Error("Expected data is undefined");
    }

    if (!taskType.isVoice && !taskType.isChat) {
        return () => Promise.reject(new Error("Send link is only available for voice or chat tasks"));
    }

    const sendComplianceEvent = () => {
        if (!taskType.isVoice || !isRealTimeComplianceCheckEnabled || !gaiaWebsocket || gaiaWebsocket.loading) {
            return;
        }

        if (complianceEventSentSessions.has(sessionId)) {
            logger.debug("Compliance event already sent for this session, skipping");
            return;
        }

        const gaiaPayload = {
            action: "orchestrator-invoke",
            appName: "soluto-sales-compliance-cue",
            messageType: "Notification",
            correlationId: crypto.randomUUID(),
            messageId: crypto.randomUUID(),
            message: "",
            sessionId: activeChatId ?? sessionId,
            sessionGroupId: sessionId,
            userId: agentId,
            userType: "Expert",
            partner,
        };

        try {
            gaiaWebsocket.sendJsonMessage(gaiaPayload);
            complianceEventSentSessions.add(sessionId);
            logger.info("Successfully sent compliance validation event to GAIA");
        } catch (error) {
            logger.warn({ error }, "Failed to send compliance validation event to GAIA");
        }
    };

    return async (commType: string, contactToSend: string): Promise<SendNotificationResponse> => {
        sendComplianceEvent();

        return sendNotification(apiClients.sales, logger, analytics.dispatcher, {
            sessionId,
            partner,
            productOfferId: selectedProduct?.id ?? primaryProducts[0].id,
            productSku: selectedProduct?.productSku ?? primaryProducts[0].productSku,
            customerEmailId: commType === "EMAIL" && contactToSend ? contactToSend : undefined,
            customerMdn: commType === "SMS" && contactToSend ? contactToSend : undefined,
            customerChatId: activeChatId,
            workerSid: agentId,
            topic: taskType.isChat ? taskData.chatTopic : undefined,
            language: taskType.isChat ? taskData.chatLanguage : undefined,
            guidedBrowsing: isGuidedBrowsing ? true : undefined,
        });
    };
};
